import React, { useState } from 'react';
import {useTranslation} from 'react-i18next';

import '../../Custom.css';
import {
    DefaultButton,
    Dropdown,
    IComboBoxOption,
    IconButton,
    Modal,
    PrimaryButton,
    Separator,
    Spinner,
    SpinnerSize,
    Stack,
    StackItem,
    Text
} from "@fluentui/react";

import {contentStyles, iconButtonStyles} from "./Style"
import StatusMessage, {StatusEnum} from "../../share/StatusMessage";

import {Controller, FormProvider, useForm} from "react-hook-form";
import {useBranding} from '../../hooks/useBranding';
import {RHFTextBoxControl} from "../RHFControls/RHFTextBoxControl";
import {decode} from 'html-entities'
import RolesService, {IPostRolesRequest} from "../../services/Roles";

type Properties = {
    showModal: boolean
    onDismissModal: (update: boolean) => void;
    templateId: string,
    stepsCBOptions: IComboBoxOption[]

}

export function RolesAdd({
                             onDismissModal,
                             showModal,
                             templateId,
                             stepsCBOptions
                         }: Properties) {

    const {t} = useTranslation(['approval', 'common'])
    const {branding} = useBranding()
    const form = useForm({
        defaultValues: {
            RoleName: '',
            ApprovalStepId: -1
        }
    })

    const [isSaving, setIsSaving] = useState(false)
    const [message, setMessage] = useState("")
    const [showMessage, setShowMessage] = useState(false)
    const [statusMessageEnum, setStatusMessageEnum] = useState<StatusEnum>(StatusEnum.Info)


    /**
     * Displays a status message with the specified content and status type.
     *
     * @function ShowStatusMessage
     * @param {string} message - The message text to be displayed.
     * @param {StatusEnum} status - An enumeration representing the type or status of the message (e.g., success, error).
     * @returns {void}
     */
    const ShowStatusMessage = (message: string, status: StatusEnum): void => {
        setMessage(decode(message))
        setStatusMessageEnum(status)
        setShowMessage(true)
    }

    /**
     * Asynchronous function to handle the creation of an approval step request.
     *
     * This function interacts with a form, retrieves form values, validates them, and submits validation errors if present.
     * Upon successful form submission, it sends a POST request to the ApprovalService to create an approval step.
     * Proper success or error messages are displayed to the user depending on the result of the request.
     *
     * The request data includes approval ID, name, and template ID.
     * The function also manages the saving state indicator and handles any errors that may occur during the request.
     *
     * Error messages and success statuses are displayed using localized strings from a translation function (`t`)
     * with appropriate namespaces (`ns`).
     *
     * @async
     * @function create
     * @returns {Promise<void>} A promise that resolves when the operation is complete.
     */
    const create = async (): Promise<void> => {
        form.clearErrors()

        await form.handleSubmit(async () => {

                setIsSaving(true)

                const requestKIM: IPostRolesRequest = {
                    Id: form.getValues("ApprovalStepId") as number,
                    RoleName: form.getValues("RoleName"),
                    TemplateId: templateId
                }

                await RolesService.postRoles(requestKIM)
                    .then(data => {
                        if (data.success) {
                            onDismissModal(true)
                        } else {
                            ShowStatusMessage(t('message.error', {
                                ns: 'common',
                                Error: data.exceptionmessage
                            }), StatusEnum.Error)
                        }
                    })
                    .catch((error) => {
                        ShowStatusMessage(t('message.error', {ns: 'common', Error: error}), StatusEnum.Error)
                    })
                    .finally(() => {
                        setIsSaving(false)
                    })

            }, () => {

            }
        )();
    }

    return (
        <>
            <Modal
                isOpen={showModal}
                isBlocking={true}
                containerClassName={contentStyles(branding.theme).addContainer}
            >

                <Stack grow={1} styles={{root: {height: "100%"}}}>
                    <Stack tokens={{childrenGap: 8}} styles={{
                        root: {
                            height: 56,
                            alignItems: 'center'
                        }
                    }} horizontal>

                        <Stack grow={1}
                               styles={{
                                   root: {
                                       textOverflow: "ellipsis",
                                       paddingLeft: 16,
                                       whiteSpace: "nowrap",
                                       overflow: "hidden"
                                   }
                               }}>
                            <Text variant={"xLarge"} id={'roledAddTitle'}
                                  title={t('addRole', {ns: 'roles'})}>
                                {t('addRole', {ns: 'roles'})}
                            </Text>
                        </Stack>

                        <Stack horizontal styles={{root: {alignItems: "center", paddingRight: 8}}}>
                            <IconButton
                                styles={iconButtonStyles}
                                iconProps={{iconName: 'Cancel'}}
                                ariaLabel={t('Common.Button.Close', {ns: 'common'})}
                                onClick={() => {
                                    onDismissModal(false)
                                }}
                                title={t('Common.Button.Close', {ns: 'common'})}
                            />
                        </Stack>
                    </Stack>

                    <Stack grow={1} styles={{root: {height: 1}}}>

                        <Stack horizontal styles={{root: {height: '100%'}}}>

                            <Stack grow={1} styles={{root: {height: '100%', width: 1}}}>

                                {/*-- START CONTENT --*/}
                                <Stack grow={1} styles={{root: {overflowY: 'auto', paddingLeft: 16, paddingRight: 16}}}
                                       className="scrollVisible">

                                    {showMessage &&
                                        <>
                                            <StackItem styles={{root: {marginBottom: 16}}}>

                                                <StatusMessage status={statusMessageEnum} hasTimer={false}
                                                               truncated={true}
                                                               isMultiline={true}
                                                               dismiss={true}
                                                               setShowMessage={setShowMessage}>
                                                    <div>{message}</div>
                                                </StatusMessage>
                                            </StackItem>

                                        </>
                                    }

                                    <FormProvider {...form}>
                                        <form>
                                            <>
                                                <Stack tokens={{childrenGap: 8}} styles={{root: {marginTop: 8}}}>

                                                    <RHFTextBoxControl
                                                        id={"RoleName"}
                                                        label={t('labelGroupName', {ns: 'roles'})}
                                                        rules={{

                                                            required: t('text.fieldRequired', {ns: 'common'}),
                                                            validate: {
                                                                required:
                                                                    (value) => {
                                                                        return !!value.trim() || t('text.fieldRequired', {ns: 'common'})
                                                                    },
                                                                roleNameError: (value) =>/^[a-zA-Z][a-zA-Z0-9_-]{0,64}$/.test(value) || t('invalidRoleName', {ns: 'roles'})
                                                            },
                                                        }}
                                                        placeholder={t('labelGroupName', {ns: 'roles'})}
                                                        title={t('labelGroupName', {ns: 'roles'})}
                                                        ariaLabel={t('labelGroupName', {ns: 'roles'})}
                                                        maxLength={25}
                                                        control={form.control}
                                                        setValue={form.setValue}
                                                        getValues={form.getValues}
                                                        trigger={form.trigger}
                                                        disabled={isSaving}
                                                        required
                                                    >
                                                    </RHFTextBoxControl>


                                                    <Controller
                                                        name="ApprovalStepId"
                                                        control={form.control}
                                                        rules={{

                                                            required: t('text.fieldRequired', {ns: 'common'}),
                                                            validate: {
                                                                required:
                                                                    (value) => {
                                                                        return value > 0 || t('text.fieldRequired', {ns: 'common'})
                                                                    }
                                                            }
                                                        }}
                                                        render={({
                                                                     field,
                                                                     fieldState: {error}
                                                                 }) =>
                                                            <Dropdown selectedKey={field.value}
                                                                      {...field}
                                                                      onChange={(_, option) => {
                                                                          field.onChange(option?.key)
                                                                      }}
                                                                      key={''}
                                                                      errorMessage={error && error.message}
                                                                      options={stepsCBOptions}
                                                                      label={t('labelApprovalStep', {ns: 'roles'})}
                                                                      disabled={isSaving}
                                                                      title={t('labelApprovalStep', {ns: 'roles'})}
                                                                      required
                                                            />}
                                                    />

                                                </Stack>
                                            </>

                                        </form>
                                    </FormProvider>


                                </Stack>
                                {/*-- END CONTENT --*/}

                                {/*-- START FOOTER --*/}
                                <Separator></Separator>
                                <Stack horizontal tokens={{childrenGap: 8}}
                                       styles={{root: {paddingRight: 16, paddingBottom: 12, alignItems: 'center'}}}>
                                    <Stack grow={1}>&nbsp;</Stack>

                                    {isSaving &&
                                        <>
                                            <StackItem>
                                                <Spinner size={SpinnerSize.medium} labelPosition="left"
                                                         label={t('text.spinnerSaving', {ns: 'common'})}/>
                                            </StackItem>
                                        </>
                                    }

                                    <StackItem>

                                        <PrimaryButton disabled={isSaving}
                                                       style={{marginLeft: 8}}
                                                       onClick={create}
                                                       text={t('Common.Button.Create', {ns: 'common'})}
                                                       title={t('Common.Button.Create', {ns: 'common'})}/>


                                        <DefaultButton
                                            onClick={() => {
                                                onDismissModal(false)
                                            }}
                                            style={{marginLeft: 8}}
                                            text={t('Common.Button.Close', {ns: 'common'})}
                                            title={t('Common.Button.Close', {ns: 'common'})}/>

                                    </StackItem>

                                </Stack>
                                {/*-- END FOOTER --*/}

                            </Stack>

                        </Stack>
                    </Stack>
                </Stack>

            </Modal>
        </>
    )
}
